/**
 * Recent Mentions List Component
 *
 * Displays the most recent mentions with business name, subreddit, confidence score,
 * and review status.
 */

import React from 'react';
import { Paper, Title, Group, Stack, Text, Badge, Loader, Table, Anchor } from '@mantine/core';
import { IconHistory } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import type { RecentMentionDto } from '../../../../domain/entities/AdminDashboard';

interface RecentMentionsListProps {
  mentions: RecentMentionDto[];
  isLoading?: boolean;
}

/**
 * Maps review status to appropriate badge color
 */
function getStatusColor(status: string): string {
  switch (status) {
    case 'Approved':
      return 'green';
    case 'Rejected':
      return 'red';
    case 'Flagged':
      return 'orange';
    case 'Pending':
    default:
      return 'blue';
  }
}

/**
 * Formats confidence score as percentage with color coding
 */
function formatConfidence(confidence: number): React.ReactElement {
  const percent = Math.round(confidence * 100);
  let color = 'gray';

  if (percent >= 90) {
    color = 'green';
  } else if (percent >= 70) {
    color = 'blue';
  } else if (percent >= 50) {
    color = 'yellow';
  } else {
    color = 'red';
  }

  return (
    <Badge color={color} variant="light" size="sm">
      {percent}%
    </Badge>
  );
}

/**
 * Formats date to relative time (e.g., "5m ago", "2h ago")
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffMins < 1440) {
    const hours = Math.floor(diffMins / 60);
    return `${hours}h ago`;
  } else {
    const days = Math.floor(diffMins / 1440);
    return `${days}d ago`;
  }
}

export const RecentMentionsList: React.FC<RecentMentionsListProps> = ({
  mentions,
  isLoading = false
}): React.ReactElement => {
  const navigate = useNavigate();

  const handleRowClick = (mentionId: string): void => {
    navigate(`/admin/mentions/review/${mentionId}`);
  };

  const handleBusinessClick = (e: React.MouseEvent): void => {
    e.stopPropagation(); // Prevent row click from firing
  };

  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Stack gap="md">
        <Group gap="xs">
          <IconHistory size={24} />
          <Title order={3}>Recent Mentions</Title>
        </Group>

        {isLoading ? (
          <Loader size="sm" />
        ) : mentions.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" mt="md">
            No recent mentions
          </Text>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Business</Table.Th>
                <Table.Th>Subreddit</Table.Th>
                <Table.Th>Confidence</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Time</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {mentions.map((mention) => (
                <Table.Tr
                  key={mention.id}
                  onClick={() => handleRowClick(mention.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td>
                    {mention.businessId ? (
                      <Anchor
                        component={Link}
                        to={`/admin/businesses/${mention.businessId}`}
                        size="sm"
                        fw={500}
                        lineClamp={1}
                        onClick={handleBusinessClick}
                      >
                        {mention.businessName}
                      </Anchor>
                    ) : (
                      <Text size="sm" c="dimmed">
                        {mention.businessName}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      r/{mention.subreddit}
                    </Text>
                  </Table.Td>
                  <Table.Td>{formatConfidence(mention.confidence)}</Table.Td>
                  <Table.Td>
                    <Badge color={getStatusColor(mention.reviewStatus)} variant="light" size="sm">
                      {mention.reviewStatus}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {formatRelativeTime(mention.createdAt)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Paper>
  );
};
